import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse,
          HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CookieStorageService } from '@sharedServices/cookie-storage.service';
import { NotificationService } from '@sharedServices/notification.service';
import { PreloaderService } from '@sharedServices/preloader.service';
import { environment } from '@environment';

export interface AppSpecificApiHeaders {
  stopLoader       ?: boolean,
  stopNotification ?: boolean
}

@Injectable()
export class Interceptor implements HttpInterceptor {

  private accessToken : string
  errorCode           : number = null

  constructor(
    private cookieStorageService: CookieStorageService,
    private notificationService: NotificationService,
    private preloaderService: PreloaderService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    let modifiedRequest : HttpRequest<any>,
        appHeaders      : AppSpecificApiHeaders = {}
     
    if (request.headers.get('appSpecificHeaders')) {
      appHeaders = JSON.parse(request.headers.get('appSpecificHeaders'))
    }
    request = request.clone({ headers: request.headers.delete('appSpecificHeaders') })
    
    if(!appHeaders?.stopLoader) {
      this.preloaderService.setLoading(true, request.url)
    }
    
    this.accessToken = this.cookieStorageService.getDecodedCookie('webApp_accessToken')

    if ((request.url.includes('/oauth/token'))) {
      modifiedRequest = request.clone({
        setHeaders: {
          'Content-Type' : 'application/x-www-form-urlencoded; charset=UTF-8',
          'Authorization': environment.basic_auth_key
        }
      })
    } else if(request.url.includes('/admin/') && request.body instanceof FormData) {
      modifiedRequest = request.clone({
        setHeaders: {
          Authorization: 'Bearer ' + this.accessToken,
        }
      })
    } else if(request.url.includes(`:${environment.nodeServerPort}/`) || request.url.includes('/admin/')
                                    || request.url.includes('/system/')) {
      let requestHeaders = {
        'Content-Type' : 'application/json',
        Authorization  : 'Bearer ' + this.accessToken
      }
      if (request.url.includes(`:${environment.nodeServerPort}/`)) {
        requestHeaders['roDetail'] = JSON.stringify({
          appVersion      : navigator.appVersion,
          cookiesEnabled  : navigator.cookieEnabled,
          language        : navigator.language,
          platform        : navigator.platform,
          productSub      : navigator.productSub,
          userAgent       : navigator.userAgent,
          vendor          : navigator.vendor
        })
        requestHeaders['roSource'] = 'WEB_APP'
      }
      modifiedRequest = request.clone({
        setHeaders: requestHeaders
      })
    } else {
      modifiedRequest = request.clone({
        setHeaders: {
          'Content-Type': 'application/json',
        }
      })
    }

    return next.handle(modifiedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if(!appHeaders?.stopLoader){
          this.preloaderService.setLoading(false, request.url)
        }
        this.errorCode = error.status
        if(appHeaders?.stopNotification) {
          return throwError(error)
        }
        if(error.status === 0) {
          this.notificationService.showError(this.getErrorMessage('Unknown/CORS'))
          return throwError(error)
        } else if (error.status === 404) {
          this.notificationService.showError(this.getErrorMessage('API Not Found'))
          return throwError(error)
        } else if (error.status === 500) {
          this.notificationService.showError(this.getErrorMessage('Unexpected Error'))
          return throwError(error)
        } else if (error.status === 503) {
          this.notificationService.showError(this.getErrorMessage('Service Unavailable'))
          return throwError(error)
        } 
        // THIS IS NOT AN ERROR, WE ARE THROWING AN ERROR SO THAT WE CAN CATCH THE RESPONSE
        else if (error.headers.get('Content-Type') === 'text/csv') {
          return throwError(error)
        } else {
          if(typeof error.error === 'string') {
            this.notificationService.showError(this.getErrorMessage('Bad Request'))
          } else if(error.error?.data?.ValidationError) {
            this.notificationService.showError(this.getErrorMessage(error.error.data.ValidationError.join(', ')))
          } else if (error.error?.data?.IncorrectData) {
            this.notificationService.showError(this.getErrorMessage(error.error.data.IncorrectData.join(', ')))
          } else {
            this.notificationService.showError(this.getErrorMessage('Bad Request'))
          }
          return throwError(error)
        }
      }),
    ).pipe(map<HttpEvent<any>, any>((event: HttpEvent<any>) => {
      if (event instanceof HttpResponse) {
        if(!appHeaders?.stopLoader){
          this.preloaderService.setLoading(false, request.url)
        }
      }
      return event
    }))
  }

  getErrorMessage(errMessage: string): string {
    return `Error Code: ${this.errorCode}<br>${errMessage}`
  }
}
